import { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button, Modal } from "../components";
import { Toggle, Slider, SettingsSection } from "../components/settings";
import { useAuth } from "../hooks/useAuth";
import { useAudio } from "../hooks/useAudio";
import { usePageTitle } from "../hooks";
import { type UserSettings, DEFAULT_USER_SETTINGS } from "@jumpbean/shared";

/**
 * SettingsPage - Comprehensive settings page for parents.
 *
 * Sections:
 * - Account settings (email, password, delete)
 * - Sound settings (volume, toggles)
 * - Display settings (accessibility options)
 * - Notification settings
 * - Subscription management
 */
export function SettingsPage() {
  usePageTitle("Settings");

  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { setVolume, playEffect } = useAudio();

  // Settings state - in a real app, this would be loaded from the API
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_USER_SETTINGS);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Subscription state (mock - would come from API)
  const [subscription] = useState({
    tier: "free" as const,
    status: "active" as const,
  });

  // Update sound system when volume changes
  useEffect(() => {
    if (settings.sound.masterVolume !== undefined) {
      setVolume(settings.sound.masterVolume / 100);
    }
  }, [settings.sound.masterVolume, setVolume]);

  // Generic settings update function
  const updateSettings = useCallback(
    <K extends keyof UserSettings>(
      section: K,
      key: keyof UserSettings[K],
      value: UserSettings[K][keyof UserSettings[K]]
    ) => {
      setSettings((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [key]: value,
        },
      }));
      setHasChanges(true);
    },
    []
  );

  // Save settings (mock implementation)
  const handleSaveSettings = useCallback(async () => {
    setIsSaving(true);
    // In a real app, this would call the API
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsSaving(false);
    setHasChanges(false);
    playEffect("correct");
  }, [playEffect]);

  // Handle volume slider end
  const handleVolumeChangeEnd = useCallback(() => {
    playEffect("click");
  }, [playEffect]);

  // Sign out handler
  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="px-4 py-6 pb-32">
      <div className="max-w-lg mx-auto space-y-6">
          {/* Save button when there are changes */}
          {hasChanges && (
          <div
            className="
              sticky top-0 z-10
              flex items-center justify-between
              px-4 py-3
              bg-sunshine-100/95 backdrop-blur-sm
              rounded-xl
              border-2 border-sunshine-300
              shadow-lg
              animate-[slideDown_200ms_ease-out]
            "
          >
            <span className="font-body font-medium text-sunshine-800">
              You have unsaved changes
            </span>
            <Button
              variant="primary"
              size="md"
              onClick={handleSaveSettings}
              isLoading={isSaving}
              className="!min-h-[48px] !px-5"
            >
              Save
            </Button>
          </div>
        )}

        {/* ======== SOUND SETTINGS ======== */}
        <SettingsSection
          title="Sound"
          description="Adjust audio preferences"
          icon={<SoundIcon />}
          accent="magic"
        >
          {/* Master Volume */}
          <Slider
            value={settings.sound.masterVolume}
            onChange={(v) => updateSettings("sound", "masterVolume", v)}
            onChangeEnd={handleVolumeChangeEnd}
            label="Master Volume"
            disabled={
              !settings.sound.musicEnabled &&
              !settings.sound.effectsEnabled &&
              !settings.sound.voiceEnabled
            }
          />

          <div className="h-px bg-neutral-100 my-2" />

          {/* Music Toggle */}
          <Toggle
            checked={settings.sound.musicEnabled}
            onChange={(v) => updateSettings("sound", "musicEnabled", v)}
            label="Background Music"
            description="Gentle melodies during gameplay"
          />

          {/* Sound Effects Toggle */}
          <Toggle
            checked={settings.sound.effectsEnabled}
            onChange={(v) => updateSettings("sound", "effectsEnabled", v)}
            label="Sound Effects"
            description="Clicks, chimes, and celebrations"
          />

          {/* Voice Prompts Toggle */}
          <Toggle
            checked={settings.sound.voiceEnabled}
            onChange={(v) => updateSettings("sound", "voiceEnabled", v)}
            label="Voice Prompts"
            description="Spoken instructions and encouragement"
          />
        </SettingsSection>

        {/* ======== DISPLAY SETTINGS ======== */}
        <SettingsSection
          title="Display"
          description="Visual accessibility options"
          icon={<DisplayIcon />}
          accent="sky"
        >
          {/* High Contrast Mode */}
          <Toggle
            checked={settings.display.highContrast}
            onChange={(v) => updateSettings("display", "highContrast", v)}
            label="High Contrast Mode"
            description="Increase color contrast for better visibility"
          />

          {/* Reduce Motion */}
          <Toggle
            checked={settings.display.reduceMotion}
            onChange={(v) => updateSettings("display", "reduceMotion", v)}
            label="Reduce Animations"
            description="Minimize motion for sensitive users"
          />

          <div className="h-px bg-neutral-100 my-2" />

          {/* Text Size */}
          <div className="space-y-3">
            <div>
              <label className="block font-body font-semibold text-body-lg text-neutral-800">
                Text Size
              </label>
              <p className="font-body text-body-md text-neutral-500">
                Adjust text size for readability
              </p>
            </div>

            <div className="flex gap-3">
              {(["normal", "large", "extra-large"] as const).map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => updateSettings("display", "textSize", size)}
                  className={`
                    flex-1
                    py-3 px-4
                    rounded-xl
                    font-body font-semibold
                    border-2
                    transition-all duration-200
                    ${
                      settings.display.textSize === size
                        ? "bg-sky-100 border-sky-400 text-sky-700 shadow-[0_2px_8px_rgba(74,144,217,0.2)]"
                        : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300"
                    }
                  `}
                >
                  <span
                    className={
                      size === "normal"
                        ? "text-sm"
                        : size === "large"
                          ? "text-base"
                          : "text-lg"
                    }
                  >
                    {size === "normal" ? "A" : size === "large" ? "A" : "A"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </SettingsSection>

        {/* ======== NOTIFICATION SETTINGS ======== */}
        <SettingsSection
          title="Notifications"
          description="Email and reminder preferences"
          icon={<NotificationIcon />}
          accent="fairy"
        >
          {/* Weekly Reports */}
          <Toggle
            checked={settings.notifications.weeklyReports}
            onChange={(v) => updateSettings("notifications", "weeklyReports", v)}
            label="Weekly Progress Reports"
            description="Receive a summary of your child's progress"
          />

          {/* Achievement Notifications */}
          <Toggle
            checked={settings.notifications.achievements}
            onChange={(v) => updateSettings("notifications", "achievements", v)}
            label="Achievement Alerts"
            description="Get notified when badges are earned"
          />

          {/* Reminders */}
          <Toggle
            checked={settings.notifications.reminders}
            onChange={(v) => updateSettings("notifications", "reminders", v)}
            label="Play Reminders"
            description="Gentle nudges to keep learning on track"
          />
        </SettingsSection>

        {/* ======== SUBSCRIPTION ======== */}
        <SettingsSection
          title="Subscription"
          description="Manage your plan"
          icon={<SubscriptionIcon />}
          accent="sunshine"
        >
          {/* Current Plan */}
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-body font-semibold text-body-lg text-neutral-800">
                Current Plan
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`
                    inline-flex items-center
                    px-3 py-1
                    rounded-full
                    font-display font-semibold text-sm
                    ${
                      subscription.tier === "free"
                        ? "bg-neutral-100 text-neutral-600"
                        : subscription.tier === "family"
                          ? "bg-sunshine-100 text-sunshine-700"
                          : "bg-magic-100 text-magic-700"
                    }
                  `}
                >
                  {subscription.tier === "free"
                    ? "Free"
                    : subscription.tier === "family"
                      ? "Family"
                      : "Classroom"}
                </span>
                {subscription.status === "active" && (
                  <span className="text-meadow-500 text-sm">Active</span>
                )}
              </div>
            </div>
          </div>

          {subscription.tier === "free" ? (
            <Link
              to="/pricing"
              className="
                block
                text-center
                py-4 px-6
                bg-gradient-to-r from-sunshine-400 to-sunshine-500
                text-neutral-900
                font-display font-bold text-body-lg
                rounded-xl
                border-2 border-sunshine-500
                shadow-[0_4px_0_0_rgba(255,193,7,1),0_6px_12px_rgba(0,0,0,0.1)]
                transition-all duration-200
                hover:shadow-[0_6px_0_0_rgba(255,193,7,1),0_8px_16px_rgba(0,0,0,0.15)]
                hover:-translate-y-0.5
                active:shadow-[0_2px_0_0_rgba(255,193,7,1)]
                active:translate-y-0.5
              "
            >
              ✨ Upgrade to Family Plan
            </Link>
          ) : (
            <button
              type="button"
              className="
                w-full
                text-left
                py-3
                font-body text-body-md text-magic-600
                hover:text-magic-700
                transition-colors
              "
            >
              Manage Billing →
            </button>
          )}
        </SettingsSection>

        {/* ======== ACCOUNT SETTINGS ======== */}
        <SettingsSection
          title="Account"
          description="Manage your account"
          icon={<AccountIcon />}
          accent="meadow"
        >
          {/* Email */}
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-body font-semibold text-body-lg text-neutral-800">
                Email Address
              </p>
              <p className="font-body text-body-md text-neutral-500">
                {user?.email || "Not set"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowEmailModal(true)}
              className="
                px-4 py-2
                font-body font-semibold text-body-md text-magic-600
                rounded-lg
                hover:bg-magic-50
                transition-colors
              "
            >
              Change
            </button>
          </div>

          <div className="h-px bg-neutral-100" />

          {/* Password */}
          <button
            type="button"
            onClick={() => setShowPasswordModal(true)}
            className="
              w-full
              flex items-center justify-between
              py-3
              font-body text-body-md text-magic-600
              hover:text-magic-700
              transition-colors
            "
          >
            <span>Change Password</span>
            <ChevronIcon />
          </button>

          <div className="h-px bg-neutral-100" />

          {/* Child Profiles Link */}
          <Link
            to="/profiles"
            className="
              flex items-center justify-between
              py-3
              font-body text-body-md text-magic-600
              hover:text-magic-700
              transition-colors
            "
          >
            <span>Manage Child Profiles</span>
            <ChevronIcon />
          </Link>

          <div className="h-px bg-neutral-100" />

          {/* Delete Account */}
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="
              w-full
              text-left
              py-3
              font-body text-body-md text-fairy-600
              hover:text-fairy-700
              transition-colors
            "
          >
            Delete Account
          </button>
        </SettingsSection>

        {/* Sign Out Button */}
        <div className="pt-4">
          <Button
            variant="secondary"
            fullWidth
            size="lg"
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </div>

        {/* App Version */}
        <p className="text-center font-body text-body-sm text-neutral-400 pt-2">
          JumpBean v1.0.0
        </p>
      </div>

      {/* ======== MODALS ======== */}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />

      {/* Change Email Modal */}
      <ChangeEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        currentEmail={user?.email}
      />

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          // In a real app, this would call the API
          setShowDeleteModal(false);
          navigate("/");
        }}
      />
    </div>
  );
}

// ======== MODAL COMPONENTS ========

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function ChangePasswordModal({ isOpen, onClose }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setIsLoading(true);
    // In a real app, this would call the API
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsLoading(false);
    onClose();
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setError("");
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Password" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block font-body font-semibold text-body-md text-neutral-700 mb-2">
            Current Password
          </label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="
              w-full
              px-4 py-3
              font-body text-body-md
              border-2 border-neutral-200
              rounded-xl
              focus:outline-none focus:border-magic-400
              transition-colors
            "
            required
          />
        </div>

        <div>
          <label className="block font-body font-semibold text-body-md text-neutral-700 mb-2">
            New Password
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="
              w-full
              px-4 py-3
              font-body text-body-md
              border-2 border-neutral-200
              rounded-xl
              focus:outline-none focus:border-magic-400
              transition-colors
            "
            required
            minLength={8}
          />
        </div>

        <div>
          <label className="block font-body font-semibold text-body-md text-neutral-700 mb-2">
            Confirm New Password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="
              w-full
              px-4 py-3
              font-body text-body-md
              border-2 border-neutral-200
              rounded-xl
              focus:outline-none focus:border-magic-400
              transition-colors
            "
            required
          />
        </div>

        {error && (
          <p className="text-fairy-600 font-body text-body-md">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            fullWidth
            size="md"
            onClick={onClose}
            className="!min-h-[56px]"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            size="md"
            isLoading={isLoading}
            className="!min-h-[56px]"
          >
            Update
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface ChangeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail?: string;
}

function ChangeEmailModal({
  isOpen,
  onClose,
  currentEmail,
}: ChangeEmailModalProps) {
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newEmail.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    // In a real app, this would call the API
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsLoading(false);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setNewEmail("");
      setPassword("");
      setError("");
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Change Email Address"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block font-body font-semibold text-body-md text-neutral-700 mb-2">
            Current Email
          </label>
          <p className="font-body text-body-md text-neutral-500">
            {currentEmail}
          </p>
        </div>

        <div>
          <label className="block font-body font-semibold text-body-md text-neutral-700 mb-2">
            New Email Address
          </label>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="
              w-full
              px-4 py-3
              font-body text-body-md
              border-2 border-neutral-200
              rounded-xl
              focus:outline-none focus:border-magic-400
              transition-colors
            "
            required
          />
        </div>

        <div>
          <label className="block font-body font-semibold text-body-md text-neutral-700 mb-2">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password to confirm"
            className="
              w-full
              px-4 py-3
              font-body text-body-md
              border-2 border-neutral-200
              rounded-xl
              focus:outline-none focus:border-magic-400
              transition-colors
            "
            required
          />
        </div>

        {error && (
          <p className="text-fairy-600 font-body text-body-md">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            fullWidth
            size="md"
            onClick={onClose}
            className="!min-h-[56px]"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            size="md"
            isLoading={isLoading}
            className="!min-h-[56px]"
          >
            Update
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function DeleteAccountModal({
  isOpen,
  onClose,
  onConfirm,
}: DeleteAccountModalProps) {
  const [confirmText, setConfirmText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async () => {
    setIsLoading(true);
    // In a real app, this would call the API
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsLoading(false);
    onConfirm();
  };

  useEffect(() => {
    if (!isOpen) {
      setConfirmText("");
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Account"
      size="sm"
      closeOnBackdropClick={false}
    >
      <div className="space-y-4">
        <div className="p-4 bg-fairy-50 rounded-xl border border-fairy-200">
          <p className="font-body text-body-md text-fairy-700">
            <strong>Warning:</strong> This action cannot be undone. All your
            data, including child profiles and progress, will be permanently
            deleted.
          </p>
        </div>

        <div>
          <label className="block font-body font-semibold text-body-md text-neutral-700 mb-2">
            Type <span className="text-fairy-600">DELETE</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="
              w-full
              px-4 py-3
              font-body text-body-md
              border-2 border-neutral-200
              rounded-xl
              focus:outline-none focus:border-fairy-400
              transition-colors
            "
            placeholder="DELETE"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            fullWidth
            size="md"
            onClick={onClose}
            className="!min-h-[56px]"
          >
            Cancel
          </Button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={confirmText !== "DELETE" || isLoading}
            className={`
              flex-1
              min-h-[56px]
              px-6
              font-display font-bold text-body-lg
              rounded-full
              border-3
              transition-all duration-200
              ${
                confirmText === "DELETE" && !isLoading
                  ? "bg-fairy-500 text-white border-fairy-600 hover:bg-fairy-600 shadow-[0_4px_0_0_rgba(192,24,91,1),0_6px_12px_rgba(0,0,0,0.15)]"
                  : "bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed"
              }
            `}
          >
            {isLoading ? "Deleting..." : "Delete Account"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ======== ICONS ========

function SoundIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
    </svg>
  );
}

function DisplayIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z" />
    </svg>
  );
}

function NotificationIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
    </svg>
  );
}

function SubscriptionIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
    </svg>
  );
}

function AccountIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      className="w-5 h-5 text-neutral-400"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

// Add slide down animation and scrollbar hide
if (typeof document !== "undefined") {
  const styleId = "jumpbean-settings-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      .scrollbar-hide::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
  }
}

export default SettingsPage;
